import React, {useState} from 'react';
import {useAuth} from '@/contexts/AuthContext.tsx';
import {updateProfile} from 'firebase/auth';
import {auth} from '@/lib/firebase/config.ts';
import AccountTypeIndicator from '../components/AccountTypeIndicator';
import AuthMethodDisplay from '../components/AuthMethodDisplay';
import {AccountType} from '../types';
import {useUserProfile} from '../hooks/useUserProfile';

const ProfileSettingsPage: React.FC = () => {
    const {user, refreshUser} = useAuth();
    const {data: userProfile, isLoading: profileLoading} = useUserProfile();

    const [displayName, setDisplayName] = useState(user?.displayName || '');
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isGuestUser = user?.email === null || user?.email === '';

    const handleSaveDisplayName = async () => {
        if (!auth.currentUser) return;

        setIsLoading(true);
        try {
            // Update Firebase Auth profile
            await updateProfile(auth.currentUser, {
                displayName: displayName.trim()
            });

            // Refresh the user data in AuthContext to update the UI
            await refreshUser();

            setIsEditing(false);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    // Determine authentication method
    const getAuthMethod = () => {
        if (isGuestUser) return 'anonymous';

        // Use Firebase's auth.currentUser to access providerData
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.providerData && currentUser.providerData.length > 0) {
            const provider = currentUser.providerData[0].providerId;
            if (provider.includes('google')) return 'google';
        }
        return 'email';
    };

    const accountType = isGuestUser ? AccountType.GUEST : AccountType.REGISTERED;
    const authMethod = getAuthMethod();

    if (profileLoading) {
        return (
            <div className="text-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading profile data...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
            </div>

            <div className="space-y-6">
                {/* Profile Photo */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Photo
                    </label>
                    <div className="flex items-center space-x-4">
                        {user?.photoURL ? (
                            <img
                                src={user.photoURL}
                                alt={user.displayName || 'Profile'}
                                className="w-16 h-16 rounded-full object-cover"
                            />
                        ) : (
                            <div
                                className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium text-xl">
                                {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                            </div>
                        )}
                        <div>
                            <button
                                type="button"
                                className="bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                disabled
                            >
                                Change Photo
                            </button>
                            <p className="text-xs text-gray-500 mt-1">Coming soon</p>
                        </div>
                    </div>
                </div>

                {/* Display Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name / Display Name
                    </label>
                    {isEditing ? (
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter your full name"
                            />
                            <button
                                onClick={handleSaveDisplayName}
                                disabled={isLoading || !displayName.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setDisplayName(user?.displayName || '');
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center space-x-2">
              <span className="text-gray-900">
                {user?.displayName || 'No name set'}
              </span>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                            >
                                Edit
                            </button>
                        </div>
                    )}
                </div>

                {/* Auth Method */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sign-in Method
                    </label>
                    <AuthMethodDisplay
                        method={authMethod as any}
                        isVerified={!isGuestUser}
                        email={user?.email || undefined}
                    />
                </div>

                {/* Account Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Type
                    </label>
                    <AccountTypeIndicator accountType={accountType}/>
                </div>

                {/* Member Since */}
                {userProfile && userProfile.createdAt && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Member Since
                        </label>
                        <span className="text-gray-900">
              {new Date(userProfile.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
              })}
            </span>
                    </div>
                )}

                {/* Delete Account Section */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
                    <div className="px-4 py-5 sm:px-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Account Data
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Delete your account
                        </p>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                        <div>
                            <button
                                className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                disabled
                            >
                                Delete Account
                            </button>
                            <p className="mt-2 text-sm text-gray-500">
                                Permanently delete your account and all associated data. This action cannot be undone.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileSettingsPage; 